import React from 'react';
import { cn } from '@/lib/utils';

interface ElegantProgressBarProps {
  progress: number;
  className?: string;
  showPercentage?: boolean;
}

export function ElegantProgressBar({ 
  progress, 
  className,
  showPercentage = true 
}: ElegantProgressBarProps) {
  // Ensure progress is between 0 and 100
  const normalizedProgress = Math.min(Math.max(progress, 0), 100);
  
  return (
    <div 
      className={cn(
        "relative h-9 w-full rounded-lg overflow-hidden",
        "bg-gradient-to-br from-zinc-900/80 to-zinc-950/90",
        "border border-zinc-700/40",
        "backdrop-blur-sm",
        "shadow-lg shadow-black/20",
        className
      )}
    >
      {/* Progress fill with elegant gradient */}
      <div
        className="absolute inset-0 bg-gradient-to-r from-amber-900/60 via-amber-700/40 to-amber-900/60 transition-all duration-500 ease-out"
        style={{ 
          width: `${normalizedProgress}%`,
          transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        {/* Shimmer effect overlay */}
        <div 
          className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-400/20 to-transparent animate-shimmer"
          style={{
            backgroundSize: '200% 100%',
            animation: 'shimmer 2s infinite'
          }}
        />
      </div>
      
      {/* Percentage text overlay */}
      {showPercentage && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xs font-light text-amber-100 tracking-wide z-10">
            {Math.round(normalizedProgress)}%
          </span>
        </div>
      )}
      
      {/* Add shimmer animation keyframes */}
      <style jsx>{`
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
    </div>
  );
}
